import type { JSX } from 'react';
import React from 'react';

import type { ISummaryBlock } from './SegmentBuilderAI.controller';

import * as styles from './SegmentBuilderAI.css';

// TODO: translate these prompts
export const EXAMPLE_PROMPTS = [
  'People who have been seen in the last 30 days',
  'People who donated more than $100 this year',
  'People who were sent email but did not open email',
  'People who have visited my donation or volunteer pages in the last 90 days',
  'People who have opened emails in the last year and are within 100 mile radius of Minneapolis',
];

// TODO: translate these statements
export const PROCESSING_STATEMENTS = [
  'Analyzing group criteria...',
  'Investigating data sources...',
  'Hacking the gibson...',
  'Scrambling data...',
  'Unscrambling data...',
  'Validating predicate logic...',
  'Preparing results...',
  'Stopping for a snack...',
];

// TODO: This whole formatting logic should be refactored when there's time
/**
 * Parse markdown-style formatting in text content
 */
function parseFormattedText(text: string): JSX.Element[] {
  const elements: JSX.Element[] = [];
  let currentIndex = 0;
  let elementKey = 0;

  // Regex to match **bold**, `code`, and *italic* patterns
  const formatRegex = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\*([^*]+)\*)/g;
  let match;

  while ((match = formatRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      elements.push(
        <span key={`text-${elementKey++}`}>{text.slice(currentIndex, match.index)}</span>,
      );
    }

    // Add formatted element
    if (match[1]) {
      // Bold text **bold**
      elements.push(<strong key={`bold-${elementKey++}`}>{match[2]}</strong>);
    } else if (match[3]) {
      // Code text `code`
      elements.push(
        <code className={styles.formattedCode} key={`code-${elementKey++}`}>
          {match[4]}
        </code>,
      );
    } else if (match[5]) {
      // Italic text *italic*
      elements.push(<em key={`italic-${elementKey++}`}>{match[6]}</em>);
    }

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (currentIndex < text.length) {
    elements.push(<span key={`text-${elementKey++}`}>{text.slice(currentIndex)}</span>);
  }

  return elements.length > 0 ? elements : [<span key={'default'}>{text}</span>];
}

/**
 * Get the appropriate heading tag based on level
 */
function getHeadingTag(level: number): 'h3' | 'h4' | 'h5' {
  if (level === 1) {
    return 'h3';
  }
  if (level === 2) {
    return 'h4';
  }
  return 'h5';
}

/**
 * Get the appropriate heading class based on level
 */
function getHeadingClass(level: number): string {
  if (level === 1) {
    return styles.formattedHeading1;
  }
  if (level === 2) {
    return styles.formattedHeading2;
  }
  return styles.formattedHeading3;
}

/**
 * Get the appropriate indent class based on level
 */
function getIndentClass(level: number): string {
  if (level <= 5) {
    return styles[`formattedIndent${level}` as keyof typeof styles];
  }
  return styles.formattedIndent5;
}

/**
 * Render a summary block with appropriate formatting
 */
function renderSummaryBlock(block: ISummaryBlock, index: number): JSX.Element {
  const { type, content, level = 1 } = block;

  switch (type) {
    case 'empty_line':
      return <div className={styles.formattedEmpty} key={`empty-${index}`} />;

    case 'heading': {
      const HeadingTag = getHeadingTag(level);
      const headingClass = getHeadingClass(level);
      return (
        <HeadingTag
          className={`${styles.formattedHeading} ${headingClass}`}
          key={`heading-${index}`}
        >
          {parseFormattedText(content)}
        </HeadingTag>
      );
    }

    case 'list_item': {
      const indentClass = getIndentClass(level);
      return (
        <div className={`${styles.formattedListItem} ${indentClass}`} key={`list-${index}`}>
          <span className={styles.formattedListBullet}>•</span>
          <span>{parseFormattedText(content)}</span>
        </div>
      );
    }

    case 'text':

    default:
      return (
        <p className={styles.formattedText} key={`text-${index}`}>
          {parseFormattedText(content)}
        </p>
      );
  }
}

/**
 * Render an array of summary blocks
 */
export function renderSummary(summary: ISummaryBlock[]): JSX.Element {
  return <div>{summary.map((block, index) => renderSummaryBlock(block, index))}</div>;
}
